import type { ListDef, FieldsOf } from "@app/schema";
import { lookupColumns, personColumns } from "@app/schema";
import { GraphClient, GraphError, odataString } from "./graph-client";

export interface ListItem<F> {
  id: number;
  /** ETag dell'elemento, da passare a update/delete per la concorrenza ottimistica. */
  etag: string;
  createdAt: string;
  modifiedAt: string;
  createdBy?: { id?: string; displayName?: string; email?: string };
  modifiedBy?: { displayName?: string; email?: string };
  /** LookupId dell'autore nel sito (presente nelle risposte di create/update). */
  authorLookupId?: number;
  fields: F;
}

/** Campi aggiornabili: `null` svuota il campo. */
export type FieldsPatch<F> = { [K in keyof F]?: F[K] | null };

/** Colonne predefinite utilizzabili in orderBy (vanno indicizzate per liste grandi). */
type BuiltInSortable = "Created" | "Modified" | "ID";

export interface QueryOptions<F> {
  /** Filtro OData sui campi, es. `fields/Stato eq 'Nuova'`. Usa `where()` per costruirlo. */
  filter?: string;
  orderBy?:
    | { field: (keyof F & string) | BuiltInSortable; desc?: boolean }
    | Array<{ field: (keyof F & string) | BuiltInSortable; desc?: boolean }>;
  top?: number;
  /**
   * Consente filtri su colonne NON indicizzate. Funziona solo sotto la soglia
   * di 5.000 elementi: usare solo per liste piccole e documentarlo.
   */
  allowNonIndexed?: boolean;
}

/**
 * Repository tipizzato per una lista SharePoint definita con `defineList`.
 * Tutte le chiamate usano il token dell'utente: i permessi SharePoint si applicano.
 */
export class ListRepository<L extends ListDef, F extends FieldsOf<L> = FieldsOf<L>> {
  private listId?: string;
  /** Colonne persona e lookup: in Graph si scrivono come `<Nome>LookupId`. */
  private readonly refs: string[];
  /**
   * Quelle a valori multipli. In lettura Graph NON restituisce `<Nome>LookupId`:
   * restituisce `<Nome>` come array di `{ LookupId, LookupValue }`.
   */
  private readonly multiRefs: string[];
  private readonly selectFields: string;

  constructor(
    private readonly graph: GraphClient,
    private readonly siteId: string,
    readonly def: L,
  ) {
    this.refs = [...personColumns(def), ...lookupColumns(def)];
    this.multiRefs = def.columns
      .filter((c) => (c.kind === "person" || c.kind === "lookup") && c.allowMultiple)
      .map((c) => c.name);
    this.selectFields = [
      "Title",
      ...def.columns.map((c) =>
        this.multiRefs.includes(c.name) ? c.name : this.refs.includes(c.name) ? `${c.name}LookupId` : c.name,
      ),
    ].join(",");
  }

  /** Id della lista (risolto dal titolo alla prima chiamata e poi in cache). */
  async id(): Promise<string> {
    if (!this.listId) {
      const list = await this.graph.request<{ id: string }>(
        `/sites/${this.siteId}/lists/${encodeURIComponent(this.def.displayName)}?$select=id`,
      );
      this.listId = list.id;
    }
    return this.listId;
  }

  private async itemsPath() {
    return `/sites/${this.siteId}/lists/${await this.id()}/items`;
  }

  /** Prima pagina di risultati + link alla successiva. */
  async query(opts: QueryOptions<F> = {}): Promise<{ items: ListItem<F>[]; nextLink?: string }> {
    const params = new URLSearchParams();
    params.set("$expand", `fields($select=${this.selectFields})`);
    params.set("$select", "id,eTag,createdDateTime,lastModifiedDateTime,createdBy,lastModifiedBy");
    params.set("$top", String(opts.top ?? 100));
    if (opts.filter) params.set("$filter", opts.filter);
    if (opts.orderBy) {
      const list = Array.isArray(opts.orderBy) ? opts.orderBy : [opts.orderBy];
      params.set("$orderby", list.map((o) => `fields/${o.field}${o.desc ? " desc" : ""}`).join(","));
    }
    const headers = opts.allowNonIndexed ? { Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly" } : undefined;
    const page = await this.graph.getPage<RawItem>(`${await this.itemsPath()}?${toQuery(params)}`, headers);
    return { items: page.items.map((r) => this.fromRaw(r)), nextLink: page.nextLink };
  }

  /** Pagina successiva, dato il nextLink restituito da query(). */
  async next(nextLink: string, allowNonIndexed = false) {
    const headers = allowNonIndexed ? { Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly" } : undefined;
    const page = await this.graph.getPage<RawItem>(nextLink, headers);
    return { items: page.items.map((r) => this.fromRaw(r)), nextLink: page.nextLink };
  }

  /** Tutti gli elementi che soddisfano la query (attenzione ai volumi). */
  async queryAll(opts: QueryOptions<F> & { max?: number } = {}): Promise<ListItem<F>[]> {
    const out: ListItem<F>[] = [];
    let page = await this.query(opts);
    out.push(...page.items);
    while (page.nextLink && (!opts.max || out.length < opts.max)) {
      page = await this.next(page.nextLink, opts.allowNonIndexed);
      out.push(...page.items);
    }
    return opts.max ? out.slice(0, opts.max) : out;
  }

  async get(id: number): Promise<ListItem<F> | undefined> {
    try {
      const raw = await this.graph.request<RawItem>(
        `${await this.itemsPath()}/${id}?$expand=fields($select=${this.selectFields})`,
      );
      return this.fromRaw(raw);
    } catch (e) {
      if (e instanceof GraphError && e.isNotFound) return undefined;
      throw e;
    }
  }

  async create(fields: F): Promise<ListItem<F>> {
    const raw = await this.graph.request<RawItem>(await this.itemsPath(), {
      method: "POST",
      body: { fields: this.toRaw(fields) },
    });
    return this.fromRaw(raw);
  }

  /**
   * Crea più elementi con $batch (20 per chiamata). Le richieste rifiutate per
   * limitazione (429/503) si ripetono; se alcune falliscono comunque, errore con il conteggio.
   */
  async createMany(items: F[]): Promise<number> {
    const path = await this.itemsPath();
    let pending = items.map((f) => ({ url: path, method: "POST" as const, body: { fields: this.toRaw(f) } }));
    let created = 0;
    for (let attempt = 0; pending.length && attempt < 4; attempt++) {
      if (attempt) await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
      const res = await this.graph.batch(pending);
      created += res.filter((r) => r.status < 300).length;
      const failed = res.map((r, i) => ({ r, req: pending[i]! })).filter((x) => x.r.status >= 300);
      const fatal = failed.find((x) => x.r.status !== 429 && x.r.status !== 503);
      if (fatal) {
        const msg = (fatal.r.body as { error?: { message?: string } } | undefined)?.error?.message ?? `HTTP ${fatal.r.status}`;
        throw new GraphError(fatal.r.status, "batchItemFailed", `${failed.length} elementi su ${items.length} non salvati: ${msg}`);
      }
      pending = failed.map((x) => x.req);
    }
    if (pending.length) throw new GraphError(429, "tooManyRequests", `${pending.length} elementi su ${items.length} non salvati: SharePoint è sovraccarico, riprovare.`);
    return created;
  }

  /**
   * Aggiorna solo i campi passati. Se `etag` è fornito e l'elemento è stato
   * modificato nel frattempo, Graph risponde 412 → GraphError.isConflict.
   */
  async update(id: number, fields: FieldsPatch<F>, etag?: string): Promise<Record<string, unknown>> {
    return this.graph.request<Record<string, unknown>>(`${await this.itemsPath()}/${id}/fields`, {
      method: "PATCH",
      body: this.toRaw(fields),
      headers: etag ? { "If-Match": etag } : undefined,
    });
  }

  async delete(id: number, etag?: string): Promise<void> {
    await this.graph.request(`${await this.itemsPath()}/${id}`, {
      method: "DELETE",
      headers: etag ? { "If-Match": etag } : undefined,
    });
  }

  /* ------------------------------------------------------------------------ */

  private fromRaw(raw: RawItem): ListItem<F> {
    const f: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(raw.fields ?? {})) {
      if (k.startsWith("@odata")) continue;
      if (this.multiRefs.includes(k)) {
        // Multi-valore: array di { LookupId, LookupValue } (persone: anche Email)
        if (Array.isArray(v)) f[k] = v.map((x) => Number(x && typeof x === "object" ? (x as { LookupId: unknown }).LookupId : x));
        else if (v == null) f[k] = [];
        continue;
      }
      const ref = this.refs.find((p) => k === `${p}LookupId`);
      if (ref) {
        // Per i multi-valore il campo LookupId non arriva di norma; se c'è, vale l'array di oggetti
        if (!(this.multiRefs.includes(ref) && Array.isArray(f[ref])))
          f[ref] = Array.isArray(v) ? v.map(Number) : v == null ? undefined : Number(v);
      } else if (!this.refs.includes(k)) f[k] = v; // il valore visualizzato di un lookup singolo si ignora
    }
    return {
      id: Number(raw.id),
      etag: raw.eTag ?? raw["@odata.etag"] ?? "",
      createdAt: raw.createdDateTime,
      modifiedAt: raw.lastModifiedDateTime,
      createdBy: raw.createdBy?.user,
      modifiedBy: raw.lastModifiedBy?.user,
      authorLookupId: f.AuthorLookupId != null ? Number(f.AuthorLookupId) : undefined,
      fields: f as F,
    };
  }

  private toRaw(fields: FieldsPatch<F>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(fields)) {
      if (v === undefined) continue;
      if (this.refs.includes(k)) {
        if (Array.isArray(v)) {
          out[`${k}LookupId@odata.type`] = "Collection(Edm.Int32)";
          out[`${k}LookupId`] = v.map(Number);
        } else {
          out[`${k}LookupId`] = v === null ? null : String(v);
        }
      } else {
        out[k] = v;
      }
    }
    return out;
  }
}

interface RawItem {
  id: string;
  eTag?: string;
  "@odata.etag"?: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  createdBy?: { user?: { id?: string; displayName?: string; email?: string } };
  lastModifiedBy?: { user?: { displayName?: string; email?: string } };
  fields?: Record<string, unknown>;
}

/** Query string con %20 al posto di "+" (Graph non interpreta sempre "+" come spazio). */
export function toQuery(p: URLSearchParams): string {
  const parts: string[] = [];
  p.forEach((v, k) => parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`));
  return parts.join("&");
}

/* -------------------------------------------------------------------------- */
/* Piccolo builder di filtri OData                                             */
/* -------------------------------------------------------------------------- */

type Scalar = string | number | boolean | Date;
const lit = (v: Scalar) =>
  typeof v === "string" ? odataString(v) : v instanceof Date ? `'${v.toISOString()}'` : String(v);

/** Costruttori di filtro tipizzati sui nomi dei campi. */
export function where<F>() {
  const f = (name: keyof F & string) => `fields/${name}`;
  return {
    eq: (name: keyof F & string, v: Scalar) => `${f(name)} eq ${lit(v)}`,
    ne: (name: keyof F & string, v: Scalar) => `${f(name)} ne ${lit(v)}`,
    gt: (name: keyof F & string, v: Scalar) => `${f(name)} gt ${lit(v)}`,
    ge: (name: keyof F & string, v: Scalar) => `${f(name)} ge ${lit(v)}`,
    lt: (name: keyof F & string, v: Scalar) => `${f(name)} lt ${lit(v)}`,
    le: (name: keyof F & string, v: Scalar) => `${f(name)} le ${lit(v)}`,
    startsWith: (name: keyof F & string, v: string) => `startswith(${f(name)}, ${lit(v)})`,
    and: (...parts: string[]) => parts.map((p) => `(${p})`).join(" and "),
    or: (...parts: string[]) => parts.map((p) => `(${p})`).join(" or "),
  };
}
